import React, { MutableRefObject, useEffect, useState } from 'react'

import { useForwardedRef } from 'utils/commonHooks'

import { EditorInputProps, InputElement } from '../types'

import { createSelectionRange } from './selection'

const measureLineHeight = (el: HTMLDivElement) => {
  const height = window.getComputedStyle(el).lineHeight
  return Number(height.replace('px', ''))
}

const EditorInput: React.ForwardRefRenderFunction<InputElement, EditorInputProps> = (
  { contentRef, onSelect, onChange, selection, disabled, readOnly, ...props },
  forwardedRef,
) => {
  const isEditingRef = React.useRef(false)
  const [elementRef, elementRefHandler] = useForwardedRef<HTMLDivElement>(
    forwardedRef as MutableRefObject<HTMLDivElement>,
  )

  const [lineHeight, setLineHeight] = useState(0)

  useEffect(() => {
    if (!elementRef.current) {
      return
    }

    setLineHeight(measureLineHeight(elementRef.current))
  }, [elementRef])

  React.useLayoutEffect(() => {
    const element = elementRef.current
    const contentElement = contentRef?.current
    if (!element || !contentElement) {
      return
    }

    isEditingRef.current = true

    const html = contentElement.innerHTML + '\n'

    if (element.innerHTML !== html) {
      element.innerHTML = html
    }

    if (element === document.activeElement) {
      const { start, end } = getSelectionCharacterOffsetWithin(element)
      if (selection.start !== start || selection.end !== end) {
        const windowSelection = window.getSelection()
        if (windowSelection) {
          const range = createSelectionRange(element, selection)
          windowSelection.removeAllRanges()
          windowSelection.addRange(range)
        }
      }
    }

    isEditingRef.current = false
  })

  const handleSelect = (event: React.SyntheticEvent<HTMLDivElement>) => {
    if (isEditingRef.current) {
      return
    }
    const { start, end } = getSelectionCharacterOffsetWithin(event.currentTarget)
    onSelect(start, end)
  }

  const handleChange = (event: React.ChangeEvent<HTMLDivElement>) => {
    const input = event.currentTarget
    const { start } = getSelectionCharacterOffsetWithin(input)
    let html = input.innerHTML
    if (html[html.length - 1] === '\n') {
      html = html.slice(0, -1)
    }

    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    onChange(tempDiv.innerText, start)
  }

  const { multiline, noWrap, rows, ...elementProps } = props // eslint-disable-line

  const minHeight = lineHeight * (!noWrap && rows ? rows : 1)

  return (
    <div
      ref={elementRefHandler}
      {...elementProps}
      contentEditable={!disabled && !readOnly}
      suppressContentEditableWarning
      onSelect={handleSelect}
      onInput={handleChange}
      data-mc-editor="true"
      style={{ minHeight: `${minHeight}px` }}
    />
  )
}

export default React.forwardRef(EditorInput)

function getSelectionCharacterOffsetWithin(element: HTMLElement) {
  let start = 0
  let end = 0
  let range: Range | undefined
  const windowSelection = window.getSelection()
  if (!windowSelection || windowSelection.rangeCount === 0) {
    return { start, end }
  }

  try {
    range = windowSelection.getRangeAt(0)
  } catch (error) {
    console.log('getRangeAt error:', error)
  }
  if (range) {
    const preCaretRange = range.cloneRange()
    preCaretRange.selectNodeContents(element)
    preCaretRange.setEnd(range.startContainer, range.startOffset)
    start = preCaretRange.toString().length
    preCaretRange.setEnd(range.endContainer, range.endOffset)
    end = preCaretRange.toString().length
  }

  return { start, end }
}
